import React from 'react';
import { Box, Typography } from '@mui/material';
import { CircleDot, DollarSign, Minus, Plus, RefreshCw } from 'lucide-react';
import { TOKENS } from '../state/colors';
import { fs } from '../state/typography';
import { formatMoneda } from '../state/format';
import { formatVariacion } from '../state/formatVersiones';
import { CambiosVersion } from '../types';

const texto = { fontFamily: 'Poppins, sans-serif' };

interface Fila {
  clave: string;
  detalle: string;
}

interface Grupo {
  etiqueta: string;
  color: string;
  Icono: typeof Plus;
  filas: Fila[];
}

// SPEC-206 (RN-011, RNF-005) — cada grupo lleva texto e ícono además del color; los vacíos no
// se muestran.
function grupos(cambios: CambiosVersion): Grupo[] {
  return [
    {
      etiqueta: 'Agregado', color: TOKENS.green, Icono: Plus,
      filas: (cambios.agregadas ?? []).map((c) => ({ clave: c.linea_id, detalle: `${c.nombre_producto} · ${c.cantidad} u · ${formatMoneda(c.precio_venta)}` })),
    },
    {
      etiqueta: 'Quitado', color: TOKENS.red, Icono: Minus,
      filas: (cambios.quitadas ?? []).map((c) => ({ clave: c.linea_id, detalle: c.nombre_producto })),
    },
    {
      etiqueta: 'Cantidad', color: TOKENS.amber, Icono: RefreshCw,
      filas: (cambios.cantidad ?? []).map((c) => ({ clave: c.linea_id, detalle: `${c.nombre_producto} · ${c.anterior} → ${c.nuevo} u` })),
    },
    {
      etiqueta: 'Precio', color: TOKENS.amber, Icono: DollarSign,
      filas: (cambios.precio ?? []).map((c) => ({ clave: c.linea_id, detalle: `${c.nombre_producto} · ${formatMoneda(c.anterior)} → ${formatMoneda(c.nuevo)}` })),
    },
    {
      etiqueta: 'Marcación', color: TOKENS.amber, Icono: CircleDot,
      filas: (cambios.marcacion ?? []).map((c) => ({
        clave: c.linea_id, detalle: `${c.nombre_producto} · ${c.anterior ?? 'Sin marcación'} → ${c.nuevo ?? 'Sin marcación'}`,
      })),
    },
  ].filter((g) => g.filas.length > 0);
}

export const VersionCambios: React.FC<{ cambios: CambiosVersion; detalleDisponible: boolean; idPanel: string }> = ({ cambios, detalleDisponible, idPanel }) => {
  const conCambios = grupos(cambios);
  const { total } = cambios;
  return (
    <Box id={idPanel} data-testid="version-cambios" sx={{ marginTop: '10px', paddingTop: '10px', borderTop: `1px solid ${TOKENS.hair}` }}>
      {conCambios.map(({ etiqueta, color, Icono, filas }) => filas.map((fila) => (
        <Box key={`${etiqueta}-${fila.clave}`} sx={{ display: 'flex', gap: '8px', alignItems: 'baseline', padding: '3px 0' }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color, minWidth: 92, flexShrink: 0 }}>
            <Icono size={13} aria-hidden data-testid={`icono-${etiqueta}`} />
            <Typography component="span" sx={{ ...texto, fontSize: fs(11.5), fontWeight: 600, color }}>{etiqueta}</Typography>
          </Box>
          <Typography component="span" sx={{ ...texto, fontSize: fs(11.5), color: TOKENS.text }}>{fila.detalle}</Typography>
        </Box>
      )))}
      {detalleDisponible && conCambios.length === 0 && (
        <Typography sx={{ ...texto, fontSize: fs(11.5), color: TOKENS.muted }}>Sin cambios en productos; solo cambió la validez</Typography>
      )}
      <Typography data-testid="version-cambios-total" sx={{ ...texto, fontSize: fs(12), fontWeight: 600, color: TOKENS.ink, marginTop: '8px' }}>
        Total {formatMoneda(total.anterior)} → {formatMoneda(total.nuevo)}
        {total.variacion_pct !== null ? ` (${formatVariacion(total.variacion_pct)})` : ''}
      </Typography>
    </Box>
  );
};
